import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';
import {
  UserCog, KeyRound, ShieldCheck, Store, ArrowUpRight, Mail,
  Building2, Landmark, Lock, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { CityCombobox } from '@/components/CityCombobox';
import { DeleteAccountSection } from '@/components/DeleteAccountSection';

const ROLE_LABEL = {
  buyer: { label: 'Comprador', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  seller: { label: 'Vendedor', cls: 'bg-orange-100 text-orange-800 border-orange-300' },
  admin: { label: 'Administrador', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
};

const EMPTY_BUSINESS = { business_name: '', nit: '', address: '', legal_rep_name: '', legal_rep_cedula: '' };
const EMPTY_BANKING = { bank_name: '', account_type: '', account_holder: '', account_number: '' };

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState({ name: '', phone: '', city: '' });
  const [pwd, setPwd] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  // Seller-only state
  const [business, setBusiness] = useState(EMPTY_BUSINESS);
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [banking, setBanking] = useState(EMPTY_BANKING);
  const [bankingSnapshot, setBankingSnapshot] = useState(null); // masked info from backend
  const [savingBanking, setSavingBanking] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [banksList, setBanksList] = useState({ banks: [], account_types: [] });
  // Facturación electrónica (config del vendedor)
  const [eInvoicing, setEInvoicing] = useState({ obligated: false, tax_regime: '', invoice_provider: '' });
  const [savingEInvoicing, setSavingEInvoicing] = useState(false);

  useSEO({
    title: 'Mi cuenta | AutoMarket Colombia',
    description: 'Actualiza tus datos personales, del negocio y tu cuenta bancaria.',
  });

  useEffect(() => {
    if (user) setProfile({ name: user.name || '', phone: user.phone || '', city: user.city || '' });
  }, [user]);

  useEffect(() => {
    if (user?.role !== 'seller') return;
    (async () => {
      try {
        const [status, banks] = await Promise.all([
          api.get('/sellers/me/onboarding-status'),
          api.get('/config/banks-list'),
        ]);
        const s = status.data?.seller || {};
        const lr = status.data?.legal_representative || {};
        setBusiness({
          business_name: s.business_name || '',
          nit: s.nit || '',
          address: s.address || '',
          legal_rep_name: lr.name || '',
          legal_rep_cedula: lr.cedula_number || '',
        });
        setBankingSnapshot(status.data?.banking || null);
        if (status.data?.banking) {
          setBanking({
            bank_name: status.data.banking.bank_name || '',
            account_type: status.data.banking.account_type || '',
            account_holder: status.data.banking.account_holder || '',
            account_number: '', // never precargar (security)
          });
        }
        setBanksList(banks.data || { banks: [], account_types: [] });
        // Carga config de facturación electrónica (endpoint específico, no
        // toca el onboarding). Fail silently si aún no está poblado.
        try {
          const ei = await api.get('/sellers/me/e-invoicing');
          const cfg = ei.data?.electronic_invoicing || {};
          setEInvoicing({
            obligated: !!cfg.obligated,
            tax_regime: cfg.tax_regime || '',
            invoice_provider: cfg.invoice_provider || '',
          });
        } catch (_) {}
      } catch (e) {
        // Fail silently — a seller that has never onboarded still sees empty forms.
      }
    })();
  }, [user]);

  if (!user) return <Layout><div className="p-12 text-center text-slate-500">Cargando...</div></Layout>;

  const isGoogle = user.auth_provider === 'google';
  const hasPassword = !!user.has_password;
  const role = ROLE_LABEL[user.role] || ROLE_LABEL.buyer;
  const isSeller = user.role === 'seller';

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch('/auth/me', profile);
      await refreshUser?.();
      toast.success('Datos actualizados correctamente');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar');
    } finally { setSavingProfile(false); }
  };

  const saveBusiness = async () => {
    if (!business.business_name.trim() || !business.nit.trim() || !business.address.trim()) {
      toast.error('Nombre del negocio, NIT y dirección son obligatorios');
      return;
    }
    setSavingBusiness(true);
    try {
      const payload = {
        business_name: business.business_name.trim(),
        nit: business.nit.trim(),
        address: business.address.trim(),
      };
      if (business.legal_rep_name.trim() || business.legal_rep_cedula.trim()) {
        payload.legal_representative = {
          name: business.legal_rep_name.trim(),
          cedula_number: business.legal_rep_cedula.trim(),
        };
      }
      await api.patch('/sellers/me', payload);
      toast.success('Datos del negocio actualizados');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar');
    } finally { setSavingBusiness(false); }
  };

  const saveBanking = async () => {
    // account_number is required only if the seller has NOT saved one previously,
    // or if they want to change it. Empty means "keep current" — we validate on backend.
    if (!banking.bank_name || !banking.account_type || !banking.account_holder.trim()) {
      toast.error('Banco, tipo de cuenta y titular son obligatorios');
      return;
    }
    const needsNumber = !bankingSnapshot?.has_account_number || banking.account_number.trim().length > 0;
    if (needsNumber) {
      const n = banking.account_number.trim();
      if (!/^\d{8,20}$/.test(n)) {
        toast.error('Número de cuenta inválido (8 a 20 dígitos)');
        return;
      }
    }
    setSavingBanking(true);
    try {
      // Banking must always be sent with the full plaintext number — the backend
      // re-encrypts. If the seller already has one saved and leaves this field
      // empty, we ask them to re-type it (defensive UX).
      if (!banking.account_number.trim() && bankingSnapshot?.has_account_number) {
        toast.error('Ingresa nuevamente el número de cuenta para confirmar el cambio');
        setSavingBanking(false);
        return;
      }
      const payload = {
        bank_name: banking.bank_name,
        account_type: banking.account_type,
        account_holder: banking.account_holder.trim(),
        account_number: banking.account_number.trim(),
      };
      const r = await api.put('/sellers/me/banking', payload);
      setBankingSnapshot(r.data?.banking || null);
      setBanking((b) => ({ ...b, account_number: '' }));
      toast.success('Datos bancarios guardados (cifrados en reposo)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally { setSavingBanking(false); }
  };

  const saveEInvoicing = async () => {
    setSavingEInvoicing(true);
    try {
      const payload = { obligated: !!eInvoicing.obligated };
      if (eInvoicing.obligated) {
        payload.tax_regime = eInvoicing.tax_regime.trim() || null;
        payload.invoice_provider = eInvoicing.invoice_provider.trim() || null;
      }
      const r = await api.put('/sellers/me/e-invoicing', payload);
      const cfg = r.data?.electronic_invoicing || {};
      setEInvoicing({
        obligated: !!cfg.obligated,
        tax_regime: cfg.tax_regime || '',
        invoice_provider: cfg.invoice_provider || '',
      });
      toast.success('Configuración de facturación guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally { setSavingEInvoicing(false); }
  };

  const changePassword = async () => {
    if (pwd.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setSavingPwd(true);
    try {
      await api.post('/auth/change-password', {
        current_password: hasPassword ? pwd.current_password : null,
        new_password: pwd.new_password,
      });
      setPwd({ current_password: '', new_password: '', confirm: '' });
      await refreshUser?.();
      toast.success(hasPassword ? 'Contraseña actualizada' : 'Contraseña creada — ya puedes ingresar también con email + contraseña');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar la contraseña');
    } finally { setSavingPwd(false); }
  };

  const upgradeToSeller = async () => {
    if (!window.confirm('¿Convertir tu cuenta a vendedor? Deberás completar la información del negocio.')) return;
    setUpgrading(true);
    try {
      await api.post('/auth/upgrade-to-seller');
      toast.success('Cuenta convertida a vendedor. Completa la información del negocio.');
      await refreshUser?.();
      nav('/vendedor/onboarding');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar el rol');
    } finally { setUpgrading(false); }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Mi cuenta</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 flex items-center gap-2">
          <UserCog className="h-7 w-7 text-slate-600" /> Mis datos
        </h1>

        {/* Identity card */}
        <section className="mt-6 border border-slate-200 bg-white p-5" data-testid="profile-identity">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5">
                <Mail className="h-3 w-3" /> Correo
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">{user.email}</div>
              <div className="mt-3 flex items-center gap-2">
                <Badge data-testid="profile-role-badge" className={`rounded-none border ${role.cls}`}>{role.label}</Badge>
                {isGoogle && (
                  <Badge variant="outline" className="rounded-none border-slate-300 text-slate-700">
                    Cuenta Google
                  </Badge>
                )}
              </div>
            </div>
            {user.role === 'buyer' && (
              <Button
                onClick={upgradeToSeller}
                disabled={upgrading}
                data-testid="profile-upgrade-seller-btn"
                variant="outline"
                className="rounded-none border-orange-500 text-orange-700 hover:bg-orange-500 hover:text-slate-900"
              >
                <Store className="h-4 w-4 mr-2" />
                {upgrading ? 'Actualizando...' : 'Quiero ser vendedor'}
                <ArrowUpRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </section>

        {/* Personal data */}
        <section className="mt-6 border border-slate-200 bg-white p-5" data-testid="profile-data-form">
          <h2 className="font-display font-bold text-slate-900 text-base uppercase tracking-wider">Datos personales</h2>
          <p className="text-xs text-slate-500 mt-1">Mantén tu información actualizada para mejorar los envíos y contacto.</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Nombre completo</label>
              <Input
                data-testid="profile-name-input"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="rounded-none mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Teléfono / WhatsApp</label>
              <Input
                data-testid="profile-phone-input"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                placeholder="+57 300 000 0000"
                className="rounded-none mt-1"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Ciudad</label>
              <div className="mt-1">
                <CityCombobox
                  testId="profile-city-combobox"
                  value={profile.city}
                  onChange={(city) => setProfile({ ...profile, city })}
                  placeholder="Escribe y selecciona tu ciudad"
                />
              </div>
            </div>
          </div>
          <Button
            data-testid="profile-save-btn"
            onClick={saveProfile}
            disabled={savingProfile}
            className="mt-4 rounded-none bg-slate-900 hover:bg-slate-800 h-11 px-6 font-bold"
          >
            {savingProfile ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </section>

        {/* Seller-only: Business info */}
        {isSeller && (
          <section className="mt-6 border border-slate-200 bg-white p-5" data-testid="profile-business-form">
            <h2 className="font-display font-bold text-slate-900 text-base uppercase tracking-wider flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Datos del negocio
            </h2>
            <p className="text-xs text-slate-500 mt-1">Actualiza la información legal y comercial de tu tienda.</p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Nombre del negocio</label>
                <Input
                  data-testid="profile-business-name-input"
                  value={business.business_name}
                  onChange={(e) => setBusiness({ ...business, business_name: e.target.value })}
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  NIT / Cédula <span className="text-red-600">*</span>
                </label>
                <Input
                  data-testid="profile-business-nit-input"
                  value={business.nit}
                  onChange={(e) => setBusiness({ ...business, nit: e.target.value })}
                  required
                  aria-required="true"
                  className="rounded-none mt-1"
                />
                <p
                  data-testid="profile-business-nit-helper"
                  className="text-[11px] text-slate-600 mt-1"
                >
                  Dato obligatorio para facturación electrónica
                </p>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Dirección</label>
                <Input
                  data-testid="profile-business-address-input"
                  value={business.address}
                  onChange={(e) => setBusiness({ ...business, address: e.target.value })}
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Representante legal (opcional)</label>
                <Input
                  data-testid="profile-legal-rep-name-input"
                  value={business.legal_rep_name}
                  onChange={(e) => setBusiness({ ...business, legal_rep_name: e.target.value })}
                  placeholder="Nombre completo"
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Cédula del representante (opcional)</label>
                <Input
                  data-testid="profile-legal-rep-cedula-input"
                  value={business.legal_rep_cedula}
                  onChange={(e) => setBusiness({ ...business, legal_rep_cedula: e.target.value })}
                  className="rounded-none mt-1"
                />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <Button
                data-testid="profile-business-save-btn"
                onClick={saveBusiness}
                disabled={savingBusiness}
                className="rounded-none bg-slate-900 hover:bg-slate-800 h-11 px-6 font-bold"
              >
                {savingBusiness ? 'Guardando...' : 'Guardar datos del negocio'}
              </Button>
              <Button
                variant="outline"
                onClick={() => nav('/vendedor/onboarding')}
                className="rounded-none border-slate-300"
                data-testid="profile-business-go-onboarding"
              >
                Ir a documentos y verificación
              </Button>
            </div>
          </section>
        )}

        {/* Seller-only: Banking info */}
        {isSeller && (
          <section className="mt-6 border border-slate-200 bg-white p-5" data-testid="profile-banking-form">
            <div className="flex items-center gap-2 flex-wrap justify-between">
              <h2 className="font-display font-bold text-slate-900 text-base uppercase tracking-wider flex items-center gap-2">
                <Landmark className="h-4 w-4" /> Cuenta bancaria para retiros
              </h2>
              <Badge variant="outline" className="rounded-none border-emerald-300 bg-emerald-50 text-emerald-700 font-normal">
                <Lock className="h-3 w-3 mr-1" /> Cifrada en reposo
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Solo tú y el equipo de finanzas pueden ver estos datos. El número se guarda cifrado con AES.
            </p>
            {bankingSnapshot?.has_account_number && (
              <div className="mt-3 border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="profile-banking-current">
                Actualmente registrada: <span className="font-mono font-bold">{bankingSnapshot.bank_name}</span> · {bankingSnapshot.account_type} · <span className="font-mono">{bankingSnapshot.account_number_masked}</span>
              </div>
            )}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Banco</label>
                <Select value={banking.bank_name} onValueChange={(v) => setBanking({ ...banking, bank_name: v })}>
                  <SelectTrigger data-testid="profile-banking-bank" className="rounded-none mt-1"><SelectValue placeholder="Selecciona el banco" /></SelectTrigger>
                  <SelectContent>
                    {banksList.banks.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Tipo de cuenta</label>
                <Select value={banking.account_type} onValueChange={(v) => setBanking({ ...banking, account_type: v })}>
                  <SelectTrigger data-testid="profile-banking-type" className="rounded-none mt-1"><SelectValue placeholder="Selecciona el tipo" /></SelectTrigger>
                  <SelectContent>
                    {banksList.account_types.map((t) => (
                      <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Titular</label>
                <Input
                  data-testid="profile-banking-holder-input"
                  value={banking.account_holder}
                  onChange={(e) => setBanking({ ...banking, account_holder: e.target.value })}
                  placeholder="Nombre completo del titular"
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  Número de cuenta {bankingSnapshot?.has_account_number ? '(reingrésalo para actualizar)' : ''}
                </label>
                <div className="mt-1 relative">
                  <Input
                    data-testid="profile-banking-number-input"
                    type={showAccountNumber ? 'text' : 'password'}
                    inputMode="numeric"
                    value={banking.account_number}
                    onChange={(e) => setBanking({ ...banking, account_number: e.target.value.replace(/\D/g, '') })}
                    placeholder={bankingSnapshot?.has_account_number ? bankingSnapshot.account_number_masked : '8 a 20 dígitos'}
                    className="rounded-none pr-10 font-mono"
                    maxLength={20}
                  />
                  <button
                    type="button"
                    data-testid="profile-banking-number-toggle"
                    onClick={() => setShowAccountNumber((v) => !v)}
                    aria-label={showAccountNumber ? 'Ocultar número' : 'Mostrar número'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-900"
                  >
                    {showAccountNumber ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            <Button
              data-testid="profile-banking-save-btn"
              onClick={saveBanking}
              disabled={savingBanking}
              className="mt-4 rounded-none bg-slate-900 hover:bg-slate-800 h-11 px-6 font-bold"
            >
              {savingBanking ? 'Guardando...' : 'Guardar cuenta bancaria'}
            </Button>
          </section>
        )}

        {/* Facturación electrónica (seller) */}
        {isSeller && (
          <section className="mt-6 border border-slate-200 bg-white p-5" data-testid="profile-einvoicing">
            <h2 className="font-display font-bold text-slate-900 text-base uppercase tracking-wider flex items-center gap-2">
              🧾 Facturación electrónica
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Marca esta casilla si estás obligado a facturar electrónicamente. Podrás adjuntar
              la factura de cada venta desde el detalle del pedido. AutoMarket <strong>no emite</strong>
              la factura por ti — solo la entrega al comprador y guarda su rastro.
            </p>
            <label className="mt-4 flex items-center gap-3 cursor-pointer" data-testid="profile-einvoicing-toggle-label">
              <input
                type="checkbox"
                data-testid="profile-einvoicing-obligated"
                checked={eInvoicing.obligated}
                onChange={(e) => setEInvoicing({ ...eInvoicing, obligated: e.target.checked })}
                className="h-4 w-4 accent-orange-600"
              />
              <span className="text-sm text-slate-900 font-medium">
                Estoy obligado a facturar electrónicamente
              </span>
            </label>
            {eInvoicing.obligated && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                    Régimen tributario (opcional)
                  </label>
                  <Input
                    data-testid="profile-einvoicing-regime"
                    value={eInvoicing.tax_regime}
                    onChange={(e) => setEInvoicing({ ...eInvoicing, tax_regime: e.target.value })}
                    placeholder="Ej. Responsable de IVA"
                    className="rounded-none mt-1"
                    maxLength={60}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                    Proveedor tecnológico (opcional)
                  </label>
                  <Input
                    data-testid="profile-einvoicing-provider"
                    value={eInvoicing.invoice_provider}
                    onChange={(e) => setEInvoicing({ ...eInvoicing, invoice_provider: e.target.value })}
                    placeholder="Ej. Siigo, Alegra, Facture..."
                    className="rounded-none mt-1"
                    maxLength={60}
                  />
                </div>
              </div>
            )}
            <Button
              data-testid="profile-einvoicing-save-btn"
              onClick={saveEInvoicing}
              disabled={savingEInvoicing}
              className="mt-4 rounded-none bg-slate-900 hover:bg-slate-800 h-11 px-6 font-bold"
            >
              {savingEInvoicing ? 'Guardando...' : 'Guardar configuración'}
            </Button>
          </section>
        )}

        {/* Password */}
        <section className="mt-6 border border-slate-200 bg-white p-5" data-testid="profile-password-form">
          <h2 className="font-display font-bold text-slate-900 text-base uppercase tracking-wider flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> {hasPassword ? 'Cambiar contraseña' : 'Crear contraseña'}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {hasPassword
              ? 'Al cambiarla, se cerrarán todas tus otras sesiones activas.'
              : 'Estás autenticado con Google. Crea una contraseña para iniciar sesión también con email.'}
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {hasPassword && (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Contraseña actual</label>
                <Input
                  data-testid="profile-current-password-input"
                  type="password"
                  value={pwd.current_password}
                  onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                  className="rounded-none mt-1"
                />
              </div>
            )}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Nueva contraseña</label>
              <Input
                data-testid="profile-new-password-input"
                type="password"
                value={pwd.new_password}
                onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                placeholder="Mín. 6 caracteres"
                className="rounded-none mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Confirmar</label>
              <Input
                data-testid="profile-confirm-password-input"
                type="password"
                value={pwd.confirm}
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                className="rounded-none mt-1"
              />
            </div>
          </div>
          <Button
            data-testid="profile-change-password-btn"
            onClick={changePassword}
            disabled={savingPwd}
            className="mt-4 rounded-none bg-slate-900 hover:bg-slate-800 h-11 px-6 font-bold"
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            {savingPwd ? 'Procesando...' : (hasPassword ? 'Actualizar contraseña' : 'Crear contraseña')}
          </Button>
        </section>

        <DeleteAccountSection hasPassword={hasPassword} />
      </div>
    </Layout>
  );
}
